import { HTMLAttributes, PropsWithChildren, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import useStableViewport from '@/hooks/use-stable-viewport'

import { cn } from '@/lib/utils'

gsap.registerPlugin(ScrollTrigger)

const interpolate = (start: number, end: number, progress: number) =>
  start + (end - start) * progress

export type ParallaxImageProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> &
    Partial<{
      offset: number
      disabled: boolean
      contentClassName: string
      animated?: boolean
    }>
>

/**
 * ParallaxImage
 *
 * Wrapper per applicare un leggero parallasse verticale ai contenuti (di solito immagini)
 * durante lo scroll.
 *
 * Props:
 * - `offset`: intensita' del movimento in pixel (default: 80)
 * - `disabled`: disattiva l'effetto mantenendo invariato il layout
 * - `className`: classi del wrapper esterno (include gia' `overflow-hidden`)
 * - `contentClassName`: classi del contenitore interno animato
 *
 * Utilizzo:
 * ```tsx
 * <ParallaxImage offset={100}>
 *   <img src="/path/to/image.jpg" alt="Parallax" />
 * </ParallaxImage>
 * ```
 */

const ParallaxImage = ({
  children,
  className,
  contentClassName,
  offset = 50,
  disabled = false,
  animated = false,
  ...props
}: ParallaxImageProps) => {
  const rootRef = useRef<HTMLDivElement>(null!)
  const contentRef = useRef<HTMLDivElement>(null!)
  const { isMobile } = useStableViewport()

  useGSAP(
    () => {
      const root = rootRef.current
      const content = contentRef.current
      if (!root || !content || disabled || isMobile) return

      if (isMobile) {
        gsap.set(content, { clearProps: 'transform,willChange' })
        return
      }

      ScrollTrigger.saveStyles([root, content])

      gsap.set(content, {
        willChange: 'transform'
      })

      // Reveal animation — once, when entering the viewport
      if (animated) {
        gsap.fromTo(
          root,
          { opacity: 0, y: '20rem' },
          {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: root,
              start: 'top -10%',
              once: true
            }
          }
        )
      }

      const setParallaxFromProgress = (progress: number) => {
        gsap.set(content, {
          y: interpolate(offset, -offset, progress)
        })
      }

      const parallaxTrigger = ScrollTrigger.create({
        trigger: root,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        invalidateOnRefresh: true,
        onRefresh: (self) => {
          setParallaxFromProgress(self.progress)
        },
        onUpdate: (self) => {
          setParallaxFromProgress(self.progress)
        }
      })

      setParallaxFromProgress(parallaxTrigger.progress)

      return () => {
        parallaxTrigger.kill()
        gsap.set(content, { clearProps: 'transform,willChange' })
      }
    },
    {
      scope: rootRef,
      dependencies: [offset, disabled, animated, isMobile],
      revertOnUpdate: true
    }
  )

  return (
    <div
      ref={rootRef}
      className={cn('relative overflow-hidden', className)}
      {...props}
    >
      <div
        ref={contentRef}
        className={cn(
          'absolute inset-0 transform-gpu will-change-transform',
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  )
}

export default ParallaxImage
